import { isEqual } from 'lodash';
import { useIntl } from 'react-intl';
import { targetingConstants } from 'sonata-platform-components';
import { Checkbox, IconTooltip, Range, Selector, SelectorOptionPropsInterface } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { languages as languageOptions } from 'sonata-static-storage/lib/samples/languages.samples';

import { FormExpandableSection } from '@/components/common/FormExpandableSection/FormExpandableSection';
import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { TargetingCard } from '../common/TargetingCard';
import { type GenderCardId, GenderCards } from './GenderCards';
import { getDemographyTags } from './helpers/getSectionHeaderTags.helper';

import './demography.scss';

import type { Language } from 'sonata-static-storage/lib/samples/languages.samples';

const { DEFAULT_DEMOGRAPHY } = targetingConstants;

export const TARGETING_DEMOGRAPHY_MAX_AGE = 100;
export const TARGETING_DEMOGRAPHY_MIN_AGE = 18;

const AGE_ALL_CARD_ICON = 'fa-solid fa-id-card';

function Demography() {
  const { setProp, setUnknownIncluded, targeting, resetSection, user, userRoles } = useTargetingContext();

  const { ages, gender, languages, unknownIncluded } = targeting;
  const { formatMessage } = useIntl();

  const unknownIncludedPermission = checkUserResourcePermissions('unknownIncluded', user, userRoles);

  const agesSelected =
    !ages[0].max.length || !ages[0].min.length
      ? [TARGETING_DEMOGRAPHY_MIN_AGE, TARGETING_DEMOGRAPHY_MAX_AGE]
      : [parseInt(ages[0].min), parseInt(ages[0].max)];

  const languagesSelected =
    languages.length > 0
      ? languages.map((language: Language) => ({
          id: language.code,
          label: language.name
        }))
      : [];

  const genderSelected: GenderCardId | undefined = isEqual(gender, DEFAULT_DEMOGRAPHY.gender)
    ? 'all'
    : (['f', 'm'] as const).find(id => id === gender[0]);

  const isAllAgesSelected = isEqual(agesSelected, [TARGETING_DEMOGRAPHY_MIN_AGE, TARGETING_DEMOGRAPHY_MAX_AGE]);

  const languagesOptions = languageOptions.map((language: Language) => ({
    id: language.code,
    label: language.name
  }));

  const handleAgesChange = (values: number | number[]) => {
    const [max, min] = Array.isArray(values) ? [values[1], values[0]] : [values, values];

    const agesFormatted = [
      {
        max: max.toString(),
        min: min.toString()
      }
    ];
    setProp('ages', agesFormatted);
  };

  const handleAllAgesClick = () => handleAgesChange([TARGETING_DEMOGRAPHY_MIN_AGE, TARGETING_DEMOGRAPHY_MAX_AGE]);

  const handleUnknownIncludedChange = (_id: string, checked: boolean) => {
    setUnknownIncluded('userAge', checked);
    setUnknownIncluded('userGender', checked);
  };

  const handleResetAgeGender = () => {
    resetSection('demographyAgeGender');
  };

  const handleLanguagesChange = (values: SelectorOptionPropsInterface[]) => {
    const languagesFormatted = values.map(val => ({
      code: val.id,
      name: val.label
    }));
    setProp('languages', languagesFormatted);
  };

  const handleResetDemography = () => {
    resetSection('demography');
  };

  const isSectionOpened =
    !isEqual(languages, DEFAULT_DEMOGRAPHY.languages) ||
    !isEqual(gender, DEFAULT_DEMOGRAPHY.gender) ||
    !isEqual(ages, DEFAULT_DEMOGRAPHY.ages);
  const demographyTags = getDemographyTags(ages, gender, languages);

  return (
    <div className="demography" id="demographyBox">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        onReset={handleResetDemography}
        preffixId="demography"
        tags={demographyTags}
        title={formatMessage({
          defaultMessage: 'Demography',
          id: 'targeting.demography.title'
        })}
        tooltipText={formatMessage({
          defaultMessage: 'Target users based on age, gender, and language preferences.',
          id: 'targeting.demography.tooltip'
        })}
      >
        <div className="demography__content">
          <div className="demography__genderContainer">
            <div className="demography__header">
              <span className="demography__genderTitle">
                {formatMessage({
                  defaultMessage: 'Age & Gender',
                  id: 'targeting.demography.gender.title'
                })}
              </span>
              <div className="demography__headerActions">
                <button className="demography__resetButton" onClick={handleResetAgeGender} type="button">
                  <i className="fa-solid fa-rotate" />
                  {formatMessage({
                    defaultMessage: 'Reset to default',
                    id: 'targeting.demography.reset'
                  })}
                </button>
                {unknownIncludedPermission && (
                  <>
                    <Checkbox
                      active={unknownIncluded.userAge && unknownIncluded.userGender}
                      id={'demographyUnknownIncludedCheckBox'}
                      labelText={formatMessage({
                        defaultMessage: 'Unknown Included',
                        id: 'targeting.demography.unknownIncluded.label'
                      })}
                      onClickAction={handleUnknownIncludedChange}
                    />
                    <IconTooltip
                      effect="solid"
                      event="mouseenter"
                      icon="fa-regular fa-circle-info"
                      iconColor="var(--color-sonata-primary-500) !important"
                      place="top"
                    >
                      {formatMessage({
                        defaultMessage: 'Include users whose age and gender are unknown.',
                        id: 'targeting.demography.unknownIncluded.tooltip'
                      })}
                    </IconTooltip>
                  </>
                )}
              </div>
            </div>
            <GenderCards
              className="demography__genderCardList"
              onSelect={id => setProp('gender', id === 'all' ? DEFAULT_DEMOGRAPHY.gender : [id])}
              selected={genderSelected}
            />
          </div>
          <div className="demography__ageContainer">
            <div className="demography__ageRow">
              <TargetingCard
                icon={AGE_ALL_CARD_ICON}
                isSelected={isAllAgesSelected}
                label={formatMessage({
                  defaultMessage: 'All (18+)',
                  id: 'targeting.demography.age.options.all'
                })}
                onClick={handleAllAgesClick}
                subtitle={formatMessage({
                  defaultMessage: 'Age',
                  id: 'targeting.demography.age.subtitle'
                })}
              />
              <Range
                className="demography__range"
                id={'demography__age'}
                max={TARGETING_DEMOGRAPHY_MAX_AGE}
                min={TARGETING_DEMOGRAPHY_MIN_AGE}
                onChange={handleAgesChange}
                value={agesSelected}
              >
                <Range.Box showReset={false} title="">
                  <Range.Info>
                    <Range.Info.Left>
                      {formatMessage(
                        {
                          defaultMessage: 'Min. {value}',
                          id: 'targeting.demography.age.min'
                        },
                        { value: TARGETING_DEMOGRAPHY_MIN_AGE }
                      )}
                    </Range.Info.Left>
                    <Range.Info.Center>
                      {formatMessage({
                        defaultMessage: 'To',
                        id: 'targeting.demography.age.to'
                      })}
                    </Range.Info.Center>
                    <Range.Info.Right>
                      {formatMessage({
                        defaultMessage: 'Max. 65+',
                        id: 'targeting.demography.age.max'
                      })}
                    </Range.Info.Right>
                  </Range.Info>
                  <Range.Input />
                  <Range.Bar />
                </Range.Box>
              </Range>
            </div>
          </div>
          <div className="demography__languageContainer">
            <Selector
              isClearable
              label={formatMessage({
                defaultMessage: 'Language',
                id: 'targeting.demography.language.title'
              })}
              onChange={handleLanguagesChange}
              options={languagesOptions}
              type="multiple"
              value={languagesSelected}
            />
          </div>
        </div>
      </FormExpandableSection>
    </div>
  );
}

export default Demography;
